import { ChangeDetectionStrategy, Component, inject, input, OnInit } from '@angular/core';
import { ICapacitySubCategoryDto } from '@app/shared/models/dtos/capacity-sub-category';
import { AgGridAngular } from 'ag-grid-angular';
import { GridApi, GridReadyEvent, GridSizeChangedEvent } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { ProjectScopingBrokerService } from '../../services/project-scoping-broker.service';
import { ProjectScopingService } from '../../services/project-scoping.service';
import { ProjectScopingCapacitiesGridOptions } from './columns';

@Component({
  selector: 'app-project-scoping-capacities',
  templateUrl: './project-scoping-capacities.component.html',
  styleUrls: ['./project-scoping-capacities.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgGridAngular,
  ],
})
export class ProjectScopingCapacitiesComponent implements OnInit {
  private projectScopingService = inject(ProjectScopingService);
  private broker = inject(ProjectScopingBrokerService);

  private gridApi: GridApi;

  readonly projectId = input<number>(undefined);

  public gridOptions = ProjectScopingCapacitiesGridOptions;
  public isLoading: boolean;

  private capacityCategories: Array<ICapacitySubCategoryDto>;
  private cancelChangesEventSub: Subscription;
  private saveChangesSuccessfulEventSub: Subscription;
  private removeItemEventSub: Subscription;

  public ValidationErrors: Map<string, string[]>;

  constructor() {

    this.projectScopingService.LoadingEvent.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
  }

  ngOnInit() {
    this.cancelChangesEventSub = this.broker.CancelChangesEvent.subscribe(s => {
      if (s != null) this.handleCancelChangesEvent(s);
    });

    this.saveChangesSuccessfulEventSub = this.broker.SaveChangesSuccessfulEvent.subscribe(s => {
      if (s != null) this.handleSaveChangesSuccessfulEvent(s);
    });

    this.ValidationErrors = new Map<string, Array<string>>();

    this.loadData();
  }

  ngOnDestroy() {
    if (this.cancelChangesEventSub) {
      this.cancelChangesEventSub.unsubscribe();
    }

    if (this.removeItemEventSub) {
      this.removeItemEventSub.unsubscribe();
    }

    if (this.saveChangesSuccessfulEventSub) {
      this.saveChangesSuccessfulEventSub.unsubscribe();
    }
  }

  public async loadProjectCapacities() {
    await this.projectScopingService.calculateAndSaveProjectCapacities(this.projectId()).then(() => {
      this.loadData();
    });
  }

  private async loadData() {
    this.capacityCategories = (await this.projectScopingService.findProjectCapacityCategoryDtosAsync(this.projectId())).Results;
    this.setUOMInCapacityCategoryTitles();
    this.sortCapacityCategories();
    this.loadGrid();
  }

  private setUOMInCapacityCategoryTitles() {
    this.capacityCategories.forEach(cat => {
      let unit = '';
      let capacityType = cat.CapacityCategoryDto.CapacityTypeId;

      switch (capacityType) {
        case 1:
          unit = 'BOPD';
          break;
        case 2:
          unit = 'MCFD';
          break;
        case 3:
          unit = 'BWPD';
          break;
      }

      if (unit != '') {
        cat.CapacityCategoryDto.Description = cat.CapacityCategoryDto.Description + ' (' + unit + ')';
      }
    });
  }

  private sortCapacityCategories() {
    this.capacityCategories.sort((a, b) => {
      if (a.CapacityCategoryDto.SortOrder == b.CapacityCategoryDto.SortOrder) {
        if (a.CapacitySubCategoryId == null) {
          return 1;
        } else if (b.CapacitySubCategoryId == null) {
          return -1;
        } else {
          return a.CapacitySubCategoryId < b.CapacitySubCategoryId ? -1 : 1;
        }
      }

      return a.CapacityCategoryDto.SortOrder > b.CapacityCategoryDto.SortOrder ? 1 : -1;
    });
  }

  private loadGrid() {
    if (this.gridApi?.getGridOption('rowData')?.length > 0 || this.gridApi?.isDestroyed()) {
      return;
    }
    this.gridApi?.setGridOption('rowData', this.capacityCategories);
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.loadGrid();
  }

  onGridSizeChanged(params: GridSizeChangedEvent) {
    if (this.isLoading) return;
    params.api.sizeColumnsToFit();
  }

  private handleCancelChangesEvent(e: any) {
    this.loadData();
  }

  private handleSaveChangesSuccessfulEvent(e: any) {
    if (!e) return;

    this.loadData();
  }
}
